/**
 * Отрисовка сцены краша.
 *
 * Сцена — это кривая множителя, по которой бежит котик. Пока раунд идёт,
 * котик держится на конце кривой; в момент краша он отрывается и падает
 * с ускорением, а кривая гаснет в красный.
 *
 * Масштаб осей плавно догоняет текущий множитель: без этого кривая через
 * десять секунд ушла бы далеко за пределы экрана. Плавность важна, иначе
 * график дёргается на каждом шаге масштаба.
 */

import { CrashPhase, multiplierAt, multiplierToNumber } from "@koshkodrop/shared";

import { GINGER, drawCat } from "./cat";

export interface SceneState {
  phase: CrashPhase;
  /** Миллисекунды с начала полёта. */
  elapsedMs: number;
  /** Текущий множитель в сотых. */
  multiplier: number;
  /** Точка краша в сотых, известна только после падения. */
  crashPoint: number | null;
  /** Миллисекунды с момента краша: управляет анимацией падения. */
  sinceCrashMs: number;
  /** Осталось до старта раунда, миллисекунды. */
  countdownMs: number;
  /** Игрок успел вывести: котик радуется вместо падения. */
  cashedOutAt: number | null;
}

interface Star {
  x: number;
  y: number;
  radius: number;
  alpha: number;
  twinkle: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  size: number;
  color: string;
}

export class CrashScene {
  private width = 0;
  private height = 0;
  private dpr = 1;

  private stars: Star[] = [];
  private particles: Particle[] = [];

  /** Масштаб осей сглаживается, чтобы график не прыгал. */
  private viewMultiplier = 2;
  private viewDuration = 7000;
  private lastCrashSeen: number | null = null;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.resize();
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    // Плотность выше двух не даёт заметного выигрыша, но заметно греет телефон
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.width = rect.width;
    this.height = rect.height;
    this.canvas.width = Math.floor(rect.width * this.dpr);
    this.canvas.height = Math.floor(rect.height * this.dpr);
    this.generateStars();
  }

  private generateStars(): void {
    const count = Math.floor((this.width * this.height) / 9000);
    this.stars = Array.from({ length: count }, () => ({
      x: Math.random() * this.width,
      y: Math.random() * this.height,
      radius: Math.random() * 1.3 + 0.3,
      alpha: Math.random() * 0.5 + 0.2,
      twinkle: Math.random() * Math.PI * 2,
    }));
  }

  draw(state: SceneState, time: number): void {
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;

    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    ctx.clearRect(0, 0, this.width, this.height);

    this.drawBackground(ctx, state, time);

    if (state.phase === CrashPhase.Betting) {
      this.drawWaiting(ctx, state, time);
    } else {
      this.updateView(state);
      this.drawGrid(ctx, state);
      this.drawCurve(ctx, state, time);
    }

    this.drawParticles(ctx);
    ctx.restore();
  }

  // ─── Фон ────────────────────────────────────────────────────────────────────

  private drawBackground(ctx: CanvasRenderingContext2D, state: SceneState, time: number): void {
    const crashed = state.phase === CrashPhase.Crashed && state.cashedOutAt === null;

    const gradient = ctx.createLinearGradient(0, 0, 0, this.height);
    if (crashed) {
      gradient.addColorStop(0, "#2a0d1f");
      gradient.addColorStop(1, "#10060f");
    } else {
      gradient.addColorStop(0, "#141033");
      gradient.addColorStop(0.6, "#0b0a1f");
      gradient.addColorStop(1, "#07061a");
    }
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, this.width, this.height);

    for (const star of this.stars) {
      const alpha = star.alpha * (0.65 + 0.35 * Math.sin(time / 900 + star.twinkle));
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 235, 210, ${alpha.toFixed(3)})`;
      ctx.fill();
    }

    // Вспышка в первые мгновения краша
    if (crashed && state.sinceCrashMs < 260) {
      const intensity = 1 - state.sinceCrashMs / 260;
      ctx.fillStyle = `rgba(239, 68, 68, ${(intensity * 0.28).toFixed(3)})`;
      ctx.fillRect(0, 0, this.width, this.height);
    }
  }

  /** Пока идёт приём ставок, котик сидит на земле и ждёт. */
  private drawWaiting(ctx: CanvasRenderingContext2D, state: SceneState, time: number): void {
    const groundY = this.height - 26;

    ctx.beginPath();
    ctx.moveTo(0, groundY);
    ctx.lineTo(this.width, groundY);
    ctx.strokeStyle = "rgba(255, 198, 92, 0.25)";
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 8]);
    ctx.stroke();
    ctx.setLineDash([]);

    const size = Math.min(this.height * 0.34, 96);
    drawCat(ctx, {
      x: this.width * 0.5,
      y: groundY - size * 0.32,
      size,
      pose: "sit",
      time,
    });

    const seconds = Math.max(0, state.countdownMs / 1000);
    ctx.textAlign = "center";
    ctx.fillStyle = "rgba(255, 235, 210, 0.85)";
    ctx.font = `600 ${Math.round(this.height * 0.075)}px Nunito, system-ui, sans-serif`;
    ctx.fillText("Ставки принимаются", this.width / 2, this.height * 0.2);

    ctx.fillStyle = "#ffc65c";
    ctx.font = `800 ${Math.round(this.height * 0.16)}px Nunito, system-ui, sans-serif`;
    ctx.fillText(`${seconds.toFixed(1)}`, this.width / 2, this.height * 0.38);
  }

  // ─── График ─────────────────────────────────────────────────────────────────

  /**
   * Подтягивает масштаб осей к текущему множителю. Коэффициент сглаживания
   * подобран так, чтобы кривая не выпрыгивала за край и не «дышала».
   */
  private updateView(state: SceneState): void {
    const targetMultiplier = Math.max(2, multiplierToNumber(state.multiplier) * 1.25);
    const targetDuration = Math.max(7000, state.elapsedMs * 1.15);

    const ease = 0.08;
    this.viewMultiplier += (targetMultiplier - this.viewMultiplier) * ease;
    this.viewDuration += (targetDuration - this.viewDuration) * ease;

    if (state.phase === CrashPhase.Betting) {
      this.viewMultiplier = 2;
      this.viewDuration = 7000;
    }
  }

  private padding = { left: 44, right: 26, top: 58, bottom: 34 };

  private pointFor(elapsedMs: number): { x: number; y: number } {
    const { left, right, top, bottom } = this.padding;
    const plotWidth = this.width - left - right;
    const plotHeight = this.height - top - bottom;

    const progress = Math.min(elapsedMs / this.viewDuration, 1);
    const value = multiplierToNumber(multiplierAt(elapsedMs));
    const share = (value - 1) / (this.viewMultiplier - 1);

    return {
      x: left + progress * plotWidth,
      y: top + plotHeight - Math.min(share, 1) * plotHeight,
    };
  }

  private drawGrid(ctx: CanvasRenderingContext2D, _state: SceneState): void {
    const { left, right, top, bottom } = this.padding;
    const plotHeight = this.height - top - bottom;

    ctx.font = "500 11px Nunito, system-ui, sans-serif";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";

    // Линии по круглым множителям: их число ограничено, чтобы сетка не пестрила
    const steps = 4;
    for (let i = 1; i <= steps; i += 1) {
      const value = 1 + ((this.viewMultiplier - 1) * i) / steps;
      const y = top + plotHeight - ((value - 1) / (this.viewMultiplier - 1)) * plotHeight;

      ctx.beginPath();
      ctx.moveTo(left, y);
      ctx.lineTo(this.width - right, y);
      ctx.strokeStyle = "rgba(255,255,255,0.05)";
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.fillStyle = "rgba(255,255,255,0.35)";
      ctx.fillText(`${value.toFixed(value < 10 ? 1 : 0)}x`, left - 8, y);
    }
  }

  private drawCurve(ctx: CanvasRenderingContext2D, state: SceneState, time: number): void {
    const { left, top, bottom } = this.padding;
    const crashed = state.phase === CrashPhase.Crashed;
    const lost = crashed && state.cashedOutAt === null;

    const endElapsed = crashed && state.crashPoint !== null ? state.elapsedMs : state.elapsedMs;
    const head = this.pointFor(endElapsed);

    // Заливка под кривой: дешёвый способ придать графику вес
    ctx.beginPath();
    ctx.moveTo(left, this.height - bottom);
    const segments = 48;
    for (let i = 0; i <= segments; i += 1) {
      const point = this.pointFor((endElapsed * i) / segments);
      ctx.lineTo(point.x, point.y);
    }
    ctx.lineTo(head.x, this.height - bottom);
    ctx.closePath();

    const fill = ctx.createLinearGradient(0, top, 0, this.height - bottom);
    if (lost) {
      fill.addColorStop(0, "rgba(239, 68, 68, 0.28)");
      fill.addColorStop(1, "rgba(239, 68, 68, 0)");
    } else {
      fill.addColorStop(0, "rgba(255, 198, 92, 0.3)");
      fill.addColorStop(1, "rgba(255, 198, 92, 0)");
    }
    ctx.fillStyle = fill;
    ctx.fill();

    // Сама кривая
    ctx.beginPath();
    for (let i = 0; i <= segments; i += 1) {
      const point = this.pointFor((endElapsed * i) / segments);
      if (i === 0) ctx.moveTo(point.x, point.y);
      else ctx.lineTo(point.x, point.y);
    }
    ctx.strokeStyle = lost ? "#ef4444" : "#ffc65c";
    ctx.lineWidth = 3.5;
    ctx.shadowColor = lost ? "rgba(239,68,68,0.7)" : "rgba(255,198,92,0.65)";
    ctx.shadowBlur = 14;
    ctx.stroke();
    ctx.shadowBlur = 0;

    this.drawMultiplierLabel(ctx, state);
    this.drawRunner(ctx, state, head, time);
  }

  private drawMultiplierLabel(ctx: CanvasRenderingContext2D, state: SceneState): void {
    const crashed = state.phase === CrashPhase.Crashed;
    const lost = crashed && state.cashedOutAt === null;
    const value = crashed && state.crashPoint !== null ? state.crashPoint : state.multiplier;

    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";

    const fontSize = Math.round(Math.min(this.width * 0.16, 64));
    ctx.font = `800 ${fontSize}px Nunito, system-ui, sans-serif`;
    ctx.fillStyle = lost ? "#fb7185" : state.cashedOutAt !== null ? "#4ade80" : "#fff7e8";
    ctx.shadowColor = lost ? "rgba(251,113,133,0.5)" : "rgba(255,214,138,0.45)";
    ctx.shadowBlur = 18;
    ctx.fillText(`${multiplierToNumber(value).toFixed(2)}x`, this.width / 2, this.padding.top - 8);
    ctx.shadowBlur = 0;

    if (crashed) {
      ctx.font = "700 13px Nunito, system-ui, sans-serif";
      ctx.fillStyle = lost ? "rgba(251,113,133,0.9)" : "rgba(74,222,128,0.9)";
      ctx.fillText(
        lost ? "Котик упал" : `Успел на ${multiplierToNumber(state.cashedOutAt ?? 100).toFixed(2)}x`,
        this.width / 2,
        this.padding.top + 12,
      );
    }
  }

  /** Котик на конце кривой, а после краша — в свободном падении. */
  private drawRunner(
    ctx: CanvasRenderingContext2D,
    state: SceneState,
    head: { x: number; y: number },
    time: number,
  ): void {
    const size = Math.min(this.height * 0.26, 74);
    const crashed = state.phase === CrashPhase.Crashed;

    if (!crashed) {
      // Небольшая пыль из-под лап на бегу
      if (Math.random() < 0.35) {
        this.particles.push({
          x: head.x - 6,
          y: head.y + size * 0.28,
          vx: -(Math.random() * 40 + 20),
          vy: -(Math.random() * 10),
          life: 1,
          size: Math.random() * 2.5 + 1,
          color: "rgba(255, 214, 138, 0.5)",
        });
      }

      drawCat(ctx, {
        x: head.x,
        y: head.y - size * 0.18,
        size,
        pose: "run",
        time,
      });
      return;
    }

    if (state.cashedOutAt !== null) {
      drawCat(ctx, {
        x: head.x,
        y: head.y - size * 0.2,
        size,
        pose: "cheer",
        time,
        palette: GINGER,
      });
      return;
    }

    // Падение: свободное падение с постоянным ускорением и вращением.
    // Момент отрыва совпадает с последней точкой кривой, поэтому переход
    // выглядит непрерывным.
    // Падение — главный кадр игры, поэтому котик сначала подлетает вверх и
    // только потом валится: так траектория читается целиком примерно за
    // секунду, а не исчезает за краем за пару кадров.
    const t = state.sinceCrashMs / 1000;
    const gravity = 900;

    // Подброс заметно сильнее гравитации на первой половине секунды: большинство
    // раундов лопается на низком множителе, где конец кривой у самого низа
    // сцены, и без подброса котик исчезал бы за границей мгновенно
    const fallY = head.y + 0.5 * gravity * t * t - 420 * t;
    const rotation = t * 3.2;

    // Снос вправо не должен выносить котика за границу сцены: у конца кривой
    // и без того мало места до правого края
    const fallX = Math.min(head.x + 70 * t, this.width - size * 0.4);

    if (state.sinceCrashMs < 120 && this.lastCrashSeen !== state.crashPoint) {
      this.lastCrashSeen = state.crashPoint;
      this.burst(head.x, head.y);
    }

    drawCat(ctx, {
      x: fallX,
      y: fallY,
      size,
      pose: "fall",
      time,
      rotation,
    });
  }

  // ─── Частицы ────────────────────────────────────────────────────────────────

  /** Разлёт искр в момент краша. */
  private burst(x: number, y: number): void {
    for (let i = 0; i < 26; i += 1) {
      const angle = (Math.PI * 2 * i) / 26 + Math.random() * 0.3;
      const speed = Math.random() * 220 + 90;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 60,
        life: 1,
        size: Math.random() * 3.5 + 1.5,
        color: i % 3 === 0 ? "rgba(255, 198, 92, 0.9)" : "rgba(239, 68, 68, 0.85)",
      });
    }
  }

  private drawParticles(ctx: CanvasRenderingContext2D): void {
    const dt = 1 / 60;
    const alive: Particle[] = [];

    for (const particle of this.particles) {
      particle.life -= dt * 1.6;
      if (particle.life <= 0) continue;

      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.vy += 900 * dt;

      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size * particle.life, 0, Math.PI * 2);
      ctx.fillStyle = particle.color.replace(
        /[\d.]+\)$/,
        `${(particle.life * 0.9).toFixed(2)})`,
      );
      ctx.fill();

      alive.push(particle);
    }

    // Ограничение сверху: на слабом телефоне тысяча частиц просаживает кадры
    this.particles = alive.slice(-180);
  }
}
